import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Header, Footer } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Building2, MapPin, Loader2, BadgeCheck, Star, MessageCircle, Phone, ChevronRight, Home, Crown, DollarSign, Users } from 'lucide-react';
import { API } from '../utils/api';
import NotFoundPage from './NotFoundPage';


// Helper function to get the correct image URL
function getImageUrl(url) {
  if (!url) return null;
  if (url.includes('.emergent.host/api/') || url.includes('.emergentagent.com/api/')) {
    try { url = new URL(url).pathname; } catch(e) {}
  }
  if (url.startsWith('/api/')) {
    var base = process.env.REACT_APP_BACKEND_URL;
    if (base && !base.includes('localhost') && typeof window !== 'undefined' && !base.includes(window.location.hostname)) {
      base = window.location.origin;
    }
    return (base || '') + url;
  }
  return url;
}

const MODALITY_LABELS = {
  'internacion': 'Internación',
  'ambulatorio': 'Ambulatorio',
  'mixto': 'Mixto'
};

function StarRating({ rating, size = 'sm' }) {
  const sizeClass = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star 
          key={star} 
          className={`${sizeClass} ${star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-slate-600'}`} 
        />
      ))}
    </div>
  );
}

function Breadcrumbs({ modality, modalityLabel, location, locationLabel }) {
  return (
    <nav className="flex items-center gap-2 text-sm text-slate-400 mb-6 flex-wrap">
      <Link to="/" className="hover:text-emerald-400 transition-colors flex items-center gap-1">
        <Home className="w-4 h-4" />
        <span>Inicio</span>
      </Link>
      <ChevronRight className="w-4 h-4" />
      <Link to="/centros-de-rehabilitacion-drogas-chile" className="hover:text-emerald-400 transition-colors">
        Centros
      </Link>
      {modality && (
        <>
          <ChevronRight className="w-4 h-4" />
          {location ? (
            <Link to={`/centro/${modality}`} className="hover:text-emerald-400 transition-colors">
              {modalityLabel}
            </Link>
          ) : (
            <span className="text-slate-200">{modalityLabel}</span>
          )}
        </>
      )}
      {location && (
        <>
          <ChevronRight className="w-4 h-4" />
          <span className="text-slate-200">{locationLabel}</span>
        </>
      )}
    </nav>
  );
}

function CenterCard({ center }) {
  // Use slug from database if available
  const centerSlug = center.slug || center.center_id;
  const rawImage = center.logo || (center.gallery && center.gallery[0]) || (center.images && center.images[0]) || null;
  const centerImage = getImageUrl(rawImage);
  
  return (
    <Link to={`/centros/${centerSlug}`} data-testid={`center-card-${center.center_id}`}>
      <Card className="h-full bg-white border-slate-200 hover:border-emerald-400 hover:shadow-lg transition-all duration-300 group overflow-hidden">
        {/* Image header with badges */}
        <div className="aspect-video bg-slate-100 flex items-center justify-center relative">
          {centerImage ? (
            <img src={centerImage} alt={center.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" onError={function(e) { e.target.style.display = 'none'; }} />
          ) : (
            <Building2 className="w-16 h-16 text-slate-300" />
          )}
          {/* Badges overlay */}
          <div className="absolute top-2 left-2 flex flex-wrap gap-1">
            {center.is_premium && (
              <span className="flex items-center gap-1 text-white bg-amber-500 px-2 py-0.5 rounded text-xs font-medium shadow">
                <Crown className="w-3 h-3" />Premium
              </span>
            )}
            {center.is_verified && (
              <span className="flex items-center gap-1 text-white bg-emerald-500 px-2 py-0.5 rounded text-xs font-medium shadow">
                <BadgeCheck className="w-3 h-3" />Verificado
              </span>
            )}
          </div>
          {/* Rating overlay */}
          {center.average_rating > 0 && (
            <div className="absolute bottom-2 right-2 flex items-center gap-1 bg-black/70 text-white px-2 py-1 rounded-lg text-sm font-medium">
              <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
              <span>{Number(center.average_rating).toFixed(1)}</span>
              <span className="text-slate-300 text-xs">({center.total_reviews || 0})</span>
            </div>
          )}
        </div>
        
        <CardContent className="p-4">
          {/* Location & Region */}
          <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
            <MapPin className="w-4 h-4 text-emerald-500" />
            <span className="font-medium">{center.city || 'Chile'}</span>
            {center.region && <span className="text-slate-400">• {center.region}</span>}
          </div>
          
          <h3 className="font-semibold text-base text-slate-800 group-hover:text-emerald-600 transition-colors duration-200 line-clamp-2 mb-3">
            {center.name}
          </h3>
          
          {/* Modality badges */}
          {center.modality && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {(Array.isArray(center.modality) ? center.modality : [center.modality]).map((mod, idx) => {
                const modalityColors = {
                  'Residencial': 'bg-blue-100 text-blue-800 border-blue-200',
                  'residencial': 'bg-blue-100 text-blue-800 border-blue-200',
                  'Ambulatorio': 'bg-purple-100 text-purple-800 border-purple-200',
                  'ambulatorio': 'bg-purple-100 text-purple-800 border-purple-200',
                  'Online': 'bg-cyan-100 text-cyan-800 border-cyan-200',
                  'online': 'bg-cyan-100 text-cyan-800 border-cyan-200',
                  'Mixto': 'bg-indigo-100 text-indigo-800 border-indigo-200',
                  'mixto': 'bg-indigo-100 text-indigo-800 border-indigo-200',
                  'Presencial': 'bg-green-100 text-green-800 border-green-200'
                };
                return (
                  <span key={idx} className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium border ${modalityColors[mod] || 'bg-slate-100 text-slate-700 border-slate-200'}`}>
                    <Building2 className="w-3 h-3" />
                    {mod}
                  </span>
                );
              })}
            </div>
          )}
          
          {/* Specializations - Tipo de adicción */}
          {center.specializations && center.specializations.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {center.specializations.slice(0, 3).map((spec, idx) => {
                const specColors = {
                  'Alcohol': 'bg-amber-100 text-amber-800 border-amber-200',
                  'Drogas': 'bg-red-100 text-red-800 border-red-200',
                  'Ludopatía': 'bg-pink-100 text-pink-800 border-pink-200',
                  'Tabaco': 'bg-orange-100 text-orange-800 border-orange-200'
                };
                return (
                  <span key={idx} className={`text-xs px-2 py-0.5 rounded-full font-medium border ${specColors[spec] || 'bg-rose-100 text-rose-700 border-rose-200'}`}>
                    {spec}
                  </span>
                );
              })}
              {center.specializations.length > 3 && (
                <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full">+{center.specializations.length - 3}</span>
              )}
            </div>
          )}
          
          {/* Services preview */}
          {center.services && center.services.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2">
              {center.services.slice(0, 2).map((service, idx) => (
                <span key={idx} className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded">
                  {service.length > 25 ? service.substring(0, 25) + '...' : service}
                </span>
              ))}
              {center.services.length > 2 && (
                <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-500 rounded">+{center.services.length - 2} más</span>
              )}
            </div>
          )}
          
          {/* Price range */}
          {(center.price_min || center.price_max || center.monthly_price) && (
            <div className="flex items-center gap-2 mb-2 p-2 bg-emerald-50 rounded-lg border border-emerald-200">
              <DollarSign className="w-4 h-4 text-emerald-600" />
              <span className="text-sm font-semibold text-emerald-700">
                {center.monthly_price 
                  ? `Desde $${center.monthly_price.toLocaleString('es-CL')}/mes`
                  : center.price_min && center.price_max 
                    ? `$${center.price_min.toLocaleString('es-CL')} - $${center.price_max.toLocaleString('es-CL')}/mes`
                    : center.price_min 
                      ? `Desde $${center.price_min.toLocaleString('es-CL')}/mes`
                      : `Hasta $${center.price_max.toLocaleString('es-CL')}/mes`
                }
              </span>
            </div>
          )}
          
          {/* Gender & Extra info */}
          <div className="flex flex-wrap items-center gap-2 mt-2 pt-2 border-t border-slate-100">
            {center.gender && (
              <span className="flex items-center gap-1 text-xs text-slate-600">
                <Users className="w-3 h-3" />
                {center.gender === 'hombres' ? 'Solo hombres' : center.gender === 'mujeres' ? 'Solo mujeres' : 'Mixto'}
              </span>
            )}
            {center.whatsapp && (
              <span className="flex items-center gap-1 text-xs text-green-600 ml-auto">
                <MessageCircle className="w-3 h-3" />WhatsApp
              </span>
            )}
            {center.phone && !center.whatsapp && (
              <span className="flex items-center gap-1 text-xs text-slate-500 ml-auto">
                <Phone className="w-3 h-3" />Teléfono
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

function RelatedSearches({ modality, location, filters }) {
  if (!filters) return null;
  
  const { modalities = [], cities = [], regions = [] } = filters;
  
  const otherModalities = modalities.filter(m => m.slug !== modality).slice(0, 4);
  const otherCities = modality ? cities.filter(c => c.slug !== location).slice(0, 8) : [];
  
  return (
    <div className="mt-12 space-y-8">
      {location && otherModalities.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-slate-200 mb-4">
            Otros tipos de tratamiento en {filters.cities?.find(c => c.slug === location)?.label || location}
          </h3>
          <div className="flex flex-wrap gap-2">
            {otherModalities.map((m) => (
              <Link
                key={m.slug}
                to={`/centro/${m.slug}/${location}`}
                className="px-4 py-2 bg-slate-800 hover:bg-emerald-500/20 border border-slate-700 hover:border-emerald-500/50 rounded-lg text-sm text-slate-300 hover:text-emerald-400 transition-all"
              >
                {m.label} en {filters.cities?.find(c => c.slug === location)?.label || location}
              </Link>
            ))}
          </div>
        </div>
      )}
      
      {modality && otherCities.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-slate-200 mb-4">
            Centros de {MODALITY_LABELS[modality] || modality} en otras ciudades
          </h3>
          <div className="flex flex-wrap gap-2">
            {otherCities.map((c) => (
              <Link
                key={c.slug}
                to={`/centro/${modality}/${c.slug}`}
                className="px-4 py-2 bg-slate-800 hover:bg-emerald-500/20 border border-slate-700 hover:border-emerald-500/50 rounded-lg text-sm text-slate-300 hover:text-emerald-400 transition-all"
              >
                {c.label} ({c.count})
              </Link>
            ))}
          </div>
        </div>
      )}
      
      {!modality && modalities.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-slate-200 mb-4">
            Buscar por tipo de tratamiento
          </h3>
          <div className="flex flex-wrap gap-2">
            {modalities.map((m) => (
              <Link
                key={m.slug}
                to={`/centro/${m.slug}`}
                className="px-4 py-2 bg-slate-800 hover:bg-emerald-500/20 border border-slate-700 hover:border-emerald-500/50 rounded-lg text-sm text-slate-300 hover:text-emerald-400 transition-all"
              >
                {m.label} ({m.count})
              </Link>
            ))}
          </div>
        </div>
      )}
      
      {modality && !location && cities.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-slate-200 mb-4">
            Centros de {MODALITY_LABELS[modality] || modality} por ciudad
          </h3>
          <div className="flex flex-wrap gap-2">
            {cities.map((c) => (
              <Link
                key={c.slug}
                to={`/centro/${modality}/${c.slug}`}
                className="px-4 py-2 bg-slate-800 hover:bg-emerald-500/20 border border-slate-700 hover:border-emerald-500/50 rounded-lg text-sm text-slate-300 hover:text-emerald-400 transition-all"
              >
                {c.label} ({c.count})
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function CentersByModality() {
  const { modality } = useParams();
  const navigate = useNavigate();
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState(null);
  const [selectedCity, setSelectedCity] = useState('');

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const [centersRes, filtersRes] = await Promise.all([
          axios.get(`${API}/seo/centros/${modality}?page=${page}&limit=12`),
          axios.get(`${API}/seo/centros/filters`)
        ]);
        setCenters(centersRes.data.centers || []);
        setTotalPages(centersRes.data.pages || 1);
        setTotal(centersRes.data.total || 0);
        setData(centersRes.data);
        setFilters(filtersRes.data);
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    }
    load();
  }, [modality, page]);

  const handleCityChange = (city) => {
    if (city && city !== 'all') {
      navigate(`/centro/${modality}/${city}`);
    }
    setSelectedCity(city);
  };

  // Validar modality (después de todos los hooks). Si no es válida → 404 noindex.
  if (!MODALITY_LABELS[modality]) return <NotFoundPage />;

  const modalityLabel = data?.modality_label || MODALITY_LABELS[modality] || modality;

  const seoTitle = `Centros de Rehabilitación ${modalityLabel} en Chile | Sin Adicciones`;
  const seoDescription = `Encuentra centros de rehabilitación con tratamiento ${modalityLabel.toLowerCase()} en Chile. ${total} centros verificados con reseñas y contacto directo.`;

  return (
    <div className="min-h-screen bg-slate-950">
      
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content={`https://sinadicciones.org/centro/${modality}`} />
        <meta property="og:image" content="https://sinadicciones.org/og-image.png" />
        <meta property="og:image:width" content="1280" />
        <meta property="og:image:height" content="896" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content="https://sinadicciones.org/og-image.png" />
        <link rel="canonical" href={`https://sinadicciones.org/centro/${modality}`} />
      
      <Header />
      <main className="pt-20">
        <section className="bg-slate-900 border-b border-slate-800 py-12">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <Breadcrumbs modality={modality} modalityLabel={modalityLabel} />
            <h1 className="text-3xl md:text-4xl font-bold text-slate-50 tracking-tight mb-3" data-testid="centers-seo-title">
              Centros de Rehabilitación {modalityLabel}
            </h1>
            <p className="text-slate-400 text-lg">
              {total > 0 ? `${total} centros de ${modalityLabel.toLowerCase()} en Chile` : `Centros con tratamiento ${modalityLabel.toLowerCase()}`}
            </p>
            
            {filters?.cities && filters.cities.length > 0 && (
              <div className="mt-6 flex items-center gap-3">
                <span className="text-slate-400 text-sm">Filtrar por ciudad:</span>
                <Select value={selectedCity} onValueChange={handleCityChange}>
                  <SelectTrigger className="w-48 bg-slate-800 border-slate-700 text-slate-200">
                    <MapPin className="w-4 h-4 mr-2 text-slate-500" />
                    <SelectValue placeholder="Todas las ciudades" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="all" className="text-slate-300">Todas las ciudades</SelectItem>
                    {filters.cities.map((c) => (
                      <SelectItem key={c.slug} value={c.slug} className="text-slate-300">
                        {c.label} ({c.count})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </section>

        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            {loading ? (
              <div className="flex justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
              </div>
            ) : centers.length === 0 ? (
              <div className="text-center py-20 bg-slate-900 rounded-xl border border-slate-800">
                <Building2 className="w-16 h-16 text-slate-700 mx-auto mb-4" />
                <h3 className="text-xl text-slate-50 mb-2">No se encontraron centros de {modalityLabel.toLowerCase()}</h3>
                <p className="text-slate-400 mb-6">Intenta buscar otro tipo de tratamiento</p>
                <Button asChild variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800">
                  <Link to="/centros-de-rehabilitacion-drogas-chile">Ver todos los centros</Link>
                </Button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="centers-seo-grid">
                  {centers.map((center) => (
                    <CenterCard key={center.center_id} center={center} />
                  ))}
                </div>
                
                {totalPages > 1 && (
                  <div className="flex justify-center gap-2 mt-12">
                    <Button variant="outline" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="border-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-50">Anterior</Button>
                    <span className="flex items-center px-4 text-sm text-slate-400">Página {page} de {totalPages}</span>
                    <Button variant="outline" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="border-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-50">Siguiente</Button>
                  </div>
                )}
              </>
            )}
            
            <RelatedSearches modality={modality} location={null} filters={filters} />
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

export function CentersByModalityLocation() {
  const { modality, location } = useParams();
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState(null);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const [centersRes, filtersRes] = await Promise.all([
          axios.get(`${API}/seo/centros/${modality}/${location}?page=${page}&limit=12`),
          axios.get(`${API}/seo/centros/filters`)
        ]);
        setCenters(centersRes.data.centers || []);
        setTotalPages(centersRes.data.pages || 1);
        setTotal(centersRes.data.total || 0);
        setData(centersRes.data);
        setFilters(filtersRes.data);
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    }
    load();
  }, [modality, location, page]);

  // Validar modality+location (modality debe ser válida). Si no → 404 noindex.
  if (!MODALITY_LABELS[modality]) return <NotFoundPage />;

  const modalityLabel = data?.modality_label || MODALITY_LABELS[modality] || modality;
  const locationLabel = data?.location_label || location;

  const seoTitle = `Centros de ${modalityLabel} en ${locationLabel} | Sin Adicciones`;
  const seoDescription = `${total} centros de rehabilitación con tratamiento ${modalityLabel.toLowerCase()} en ${locationLabel}, Chile. Verificados con reseñas y contacto directo.`;

  return (
    <div className="min-h-screen bg-slate-950">
      
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content={`https://sinadicciones.org/centro/${modality}/${location}`} />
        <meta property="og:image" content="https://sinadicciones.org/og-image.png" />
        <meta property="og:image:width" content="1280" />
        <meta property="og:image:height" content="896" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content="https://sinadicciones.org/og-image.png" />
        <link rel="canonical" href={`https://sinadicciones.org/centro/${modality}/${location}`} />
      
      <Header />
      <main className="pt-20">
        <section className="bg-slate-900 border-b border-slate-800 py-12">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <Breadcrumbs modality={modality} modalityLabel={modalityLabel} location={location} locationLabel={locationLabel} />
            <h1 className="text-3xl md:text-4xl font-bold text-slate-50 tracking-tight mb-3" data-testid="centers-seo-location-title">
              Centros de {modalityLabel} en {locationLabel}
            </h1>
            <p className="text-slate-400 text-lg">
              {total > 0 
                ? `${total} centros de rehabilitación ${modalityLabel.toLowerCase()} en ${locationLabel}` 
                : `Centros con tratamiento ${modalityLabel.toLowerCase()} en ${locationLabel}`
              }
            </p>
          </div>
        </section>

        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            {loading ? (
              <div className="flex justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
              </div>
            ) : centers.length === 0 ? (
              <div className="text-center py-20 bg-slate-900 rounded-xl border border-slate-800">
                <Building2 className="w-16 h-16 text-slate-700 mx-auto mb-4" />
                <h3 className="text-xl text-slate-50 mb-2">No se encontraron centros de {modalityLabel.toLowerCase()} en {locationLabel}</h3>
                <p className="text-slate-400 mb-6">Prueba buscando en otra ciudad</p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button asChild variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800">
                    <Link to={`/centro/${modality}`}>Ver en todo Chile</Link>
                  </Button>
                  <Button asChild variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800">
                    <Link to="/centros-de-rehabilitacion-drogas-chile">Ver todos los centros</Link>
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="centers-seo-location-grid">
                  {centers.map((center) => (
                    <CenterCard key={center.center_id} center={center} />
                  ))}
                </div>
                
                {totalPages > 1 && (
                  <div className="flex justify-center gap-2 mt-12">
                    <Button variant="outline" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="border-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-50">Anterior</Button>
                    <span className="flex items-center px-4 text-sm text-slate-400">Página {page} de {totalPages}</span>
                    <Button variant="outline" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="border-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-50">Siguiente</Button>
                  </div>
                )}
              </>
            )}
            
            <RelatedSearches modality={modality} location={location} filters={filters} />
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
